import { Box, ButtonBase, Typography } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ensureCitiesCached,
  getCityLabel,
  getCityStateLabel,
  searchCities,
} from '../services/patientFormCache'
import { usePatientFormI18n } from '../utils/patientFormI18n'
import { PATIENT_FORM_TOKENS as T, PatientFormField, PatientTextInput } from './PatientFormPrimitives'

const LISTBOX_ID = 'patient-city-listbox'

/**
 * City as a plain text field with a local-first suggestion list underneath.
 *
 * Suggestions come from the city database cached in IndexedDB (warmed for the clinic's
 * country on first use; API fallback while cold). Whatever the user types stays as the city,
 * so a town that is not in the database still works; picking a suggestion also fills
 * state / country. No react-select, so it behaves the same on phones and desktops.
 */
export default function PatientCityField({
  patientDetails,
  editMode = true,
  formHook,
  countryIso,
  span = { xs: 12, md: 6 },
  requiredEmpty = false,
}) {
  const { copy } = usePatientFormI18n()
  const value = patientDetails?.city || ''
  const [suggestions, setSuggestions] = useState([])
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [searching, setSearching] = useState(false)
  const requestRef = useRef(0)
  const blurTimerRef = useRef(null)

  useEffect(() => {
    if (editMode && countryIso) ensureCitiesCached(countryIso)
  }, [editMode, countryIso])

  const runSearch = useCallback(
    async (term) => {
      const requestId = requestRef.current + 1
      requestRef.current = requestId
      if (!term || term.trim().length < 2) {
        setSuggestions([])
        setSearching(false)
        return
      }
      setSearching(true)
      const rows = await searchCities(term, { iso: countryIso, limit: 8 })
      if (requestRef.current !== requestId) return
      setSuggestions(rows)
      setActiveIndex(rows.length > 0 ? 0 : -1)
      setSearching(false)
    },
    [countryIso],
  )

  useEffect(() => {
    if (!open) return undefined
    const timer = setTimeout(() => runSearch(value), 120)
    return () => clearTimeout(timer)
  }, [value, open, runSearch])

  const pick = (city) => {
    formHook.handleCitySelect(city)
    setOpen(false)
    setSuggestions([])
    setActiveIndex(-1)
  }

  const exactMatch = useMemo(() => {
    const needle = value.trim().toLowerCase()
    return suggestions.find((row) => getCityLabel(row).toLowerCase() === needle) || null
  }, [suggestions, value])

  const handleKeyDown = (event) => {
    if (!open || suggestions.length === 0) {
      if (event.key === 'ArrowDown') setOpen(true)
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((index) => (index + 1) % suggestions.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((index) => (index - 1 + suggestions.length) % suggestions.length)
    } else if (event.key === 'Enter') {
      if (activeIndex >= 0) {
        event.preventDefault()
        pick(suggestions[activeIndex])
      }
    } else if (event.key === 'Escape') {
      setOpen(false)
    }
  }

  const showList = editMode && open && suggestions.length > 0

  return (
    <PatientFormField
      label={copy.cityLabel}
      span={span}
      htmlFor="patient-city"
      requiredEmpty={requiredEmpty}
      hint={editMode && open && !searching && value.trim().length >= 2 && suggestions.length === 0 ? copy.cityNoResults : null}
    >
      <Box sx={{ position: 'relative', width: '100%', minWidth: 0 }} data-testid="patient-city-select">
        <PatientTextInput
          id="patient-city"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showList}
          aria-controls={LISTBOX_ID}
          aria-activedescendant={showList && activeIndex >= 0 ? `${LISTBOX_ID}-${activeIndex}` : undefined}
          autoComplete="off"
          value={value}
          placeholder={copy.cityPlaceholder}
          disabled={!editMode}
          onChange={(event) => {
            formHook.handleCityChange(event)
            setOpen(true)
          }}
          onFocus={() => {
            if (blurTimerRef.current) clearTimeout(blurTimerRef.current)
            setOpen(true)
          }}
          onBlur={() => {
            // Let a click on a suggestion land before the list closes.
            blurTimerRef.current = setTimeout(() => setOpen(false), 150)
          }}
          onKeyDown={handleKeyDown}
        />
        {showList && (
          <Box
            id={LISTBOX_ID}
            role="listbox"
            sx={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              left: 0,
              right: 0,
              zIndex: 30,
              maxHeight: '264px',
              overflowY: 'auto',
              background: T.surface,
              border: '1px solid #e2e8f0',
              borderRadius: '10px',
              boxShadow: '0 12px 32px rgba(15, 23, 42, 0.16)',
              padding: '4px',
            }}
          >
            {suggestions.map((city, index) => {
              const label = getCityLabel(city)
              const state = getCityStateLabel(city)
              const active = index === activeIndex
              const isExact = exactMatch && exactMatch.id === city.id
              return (
                <ButtonBase
                  key={city.id || `${label}-${index}`}
                  id={`${LISTBOX_ID}-${index}`}
                  role="option"
                  aria-selected={active}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => pick(city)}
                  sx={{
                    width: '100%',
                    justifyContent: 'space-between',
                    gap: '10px',
                    textAlign: 'left',
                    padding: { xs: '11px 12px', sm: '9px 12px' },
                    borderRadius: '8px',
                    background: active ? '#eef2ff' : 'transparent',
                    '&:hover': { background: '#f1f5f9' },
                  }}
                >
                  <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <Typography component="span" sx={{ fontSize: '14px', fontWeight: isExact ? 700 : 500, color: T.text }}>
                      {label}
                    </Typography>
                    {state && (
                      <Typography component="span" sx={{ fontSize: '12px', color: T.muted }}>
                        {state}
                      </Typography>
                    )}
                  </Box>
                  {isExact && (
                    <Typography component="span" sx={{ fontSize: '11px', fontWeight: 700, color: T.accent, whiteSpace: 'nowrap' }}>
                      {copy.cityExactMatchLabel}
                    </Typography>
                  )}
                </ButtonBase>
              )
            })}
          </Box>
        )}
      </Box>
    </PatientFormField>
  )
}
